/**
 * PendingList.tsx
 *
 * Pattern C-Lite: inline expandable list of employees stuck at a given
 * step. Designed as a drop-in for any StepCard's children slot.
 *
 * Goals (low cognitive, simple):
 *   - Default state: collapsed, header shows "▸ N pending"
 *   - One click → expands inline (no modal, no drawer, no route)
 *   - One row per employee: name + status pill + optional action
 *   - Done employees are NOT rendered here — uncluttered by design
 *   - HR can see "who's stuck" instantly without leaving the page
 *
 * Renders nothing when the items list is empty (caller can show its
 * own all-done message).
 */

import React, { useState } from 'react';
import DisclosureBadge from './DisclosureBadge';

export interface PendingListItem {
  /** Stable id used as React key. */
  id: string;
  /** Display name. Fallback to email or id when name unknown. */
  name: string;
  /** Optional secondary label (email / unit / supervisor). */
  subtitle?: string;
  /** Status text e.g. "waiting manager", "no self yet". */
  status: string;
  /** Tone for the status pill. */
  tone?: 'info' | 'warning' | 'neutral';
  /** Optional inline action button label + handler. */
  actionLabel?: string;
  onAction?: () => void;
  /** Inline material symbol on the action button. */
  actionIcon?: string;
}

export interface PendingListProps {
  /** Pending employees only — done ones must be filtered before passing. */
  items: PendingListItem[];
  /** Label shown in the toggle. Default: "pending". */
  noun?: string;
  /** Initial expanded state. Default false. */
  defaultOpen?: boolean;
  /** Empty-state message when items.length === 0. Optional. */
  emptyMessage?: string;
}

const PendingList: React.FC<PendingListProps> = ({
  items,
  noun = 'pending',
  defaultOpen = false,
  emptyMessage,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  if (items.length === 0) {
    return emptyMessage
      ? <div className="text-xs text-stone-500 mt-3">{emptyMessage}</div>
      : null;
  }

  return (
    <div className="mt-3 border-t border-stone-200 pt-3">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 text-sm text-stone-700 hover:text-stone-900 transition"
        aria-expanded={open}
      >
        <span className="material-symbols-rounded text-stone-500" style={{ fontSize: 18 }}>
          {open ? 'expand_more' : 'chevron_right'}
        </span>
        <span>
          <b>{items.length}</b> {noun}
        </span>
      </button>

      {open && (
        <ul className="mt-2 space-y-1.5">
          {items.map(it => (
            <li
              key={it.id}
              className="flex flex-wrap items-center gap-2 rounded-lg bg-stone-50 border border-stone-200 px-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-stone-900 truncate">{it.name}</div>
                {it.subtitle && (
                  <div className="text-xs text-stone-500 truncate">{it.subtitle}</div>
                )}
              </div>
              <DisclosureBadge label={it.status} tone={it.tone ?? 'warning'} />
              {it.actionLabel && it.onAction && (
                <button
                  type="button"
                  onClick={it.onAction}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-stone-300 bg-white hover:border-stone-500"
                >
                  {it.actionIcon && (
                    <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
                      {it.actionIcon}
                    </span>
                  )}
                  {it.actionLabel}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PendingList;
